// LinkableKind — Primitive 1.
// The polymorphic (kind, id) reference resolver. Every entity that can be
// embedded as a card, referenced from another module, or surfaced in a feed
// implements this contract.
//
// Frozen at schemaVersion: 1. New fields go under `metadata` or as optional;
// breaking changes require a major bump (and an ADR). See
// DOCS/architecture/primitives/linkable-kind.md.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// LinkableResource — the resolved, card-renderable shape returned by `resolve`.
// ---------------------------------------------------------------------------
export const LinkableResource = z.object({
    /** Polymorphic discriminator; matches the owning module's kind. */
    kind: z.string().min(1),
    /** Stable opaque id (UUID for most kinds; slug-derived for groups). */
    id: z.string().min(1),
    /** Canonical URL (relative or absolute). */
    url: z.string().min(1),
    /** Display title. */
    title: z.string(),
    /** One-line excerpt. ≤ 160 chars by convention. */
    summary: z.string().nullable(),
    /** Optional cover image URL. */
    cover: z.string().nullable(),
    /** Type-specific status (`published` | `open` | `solved` | `expired` | …). */
    status: z.string().nullable(),
    /** Type-specific extras. Keep small — large blobs belong on the source. */
    metadata: z.record(z.string(), z.unknown()),
    /** Groups this resource belongs to — drives canRead. */
    groupIds: z.array(z.string()),
    /** Owner / author — drives the "always allow author" canRead branch. */
    ownerId: z.string().nullable(),
    /** ISO 8601 — used for freshness display and future cache keys. */
    updatedAt: z.string().datetime({ offset: true }),
    /** Frozen at 1. */
    schemaVersion: z.literal(1),
});
/**
 * A `(kind, id)` reference. The shape used by every cross-module
 * pointer — calendar events' subjects, comment subjects, search results.
 */
export const LinkableRef = z.object({
    kind: z.string().min(1),
    id: z.string().min(1),
});
// ---------------------------------------------------------------------------
// Anchor formats — kinds may carry sub-resource anchors via the URL fragment.
// Documentation lives next to the kind owning module; this enum is permissive.
// ---------------------------------------------------------------------------
export const ANCHOR_PATTERNS = {
    article: ['#section-', '#paragraph-', '#annotation-'],
    survey: ['#question-'],
    thread: ['#post-'],
    event: ['#occurrence-'],
};
//# sourceMappingURL=linkable-kind.js.map